import { Loader2, CreditCard, Check, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/utils'
import { usePaymentPage } from './PaymentPage.logic'
import { PLAN_META, PLAN_ORDER, FEATURES } from './PaymentPage.static'
import type { Plan } from '@/services/api/subscription.service'

function PlanCard({
  plan,
  isSelected,
  onSelect,
  t,
}: {
  plan: Plan
  isSelected: boolean
  onSelect: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  const meta = PLAN_META[plan.id]
  const priceMonthly = (plan.priceMonthly / 100).toFixed(0)
  const priceTotal = (plan.priceTotal / 100).toFixed(0)

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'relative w-full rounded-lg border-2 p-4 text-left transition-all',
        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
      )}
    >
      {meta.badgeKey && (
        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs">
          {t(meta.badgeKey)}
        </Badge>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium">{t(`plans.${plan.id}.label`)}</p>
          <p className="text-muted-foreground text-xs">
            {t(meta.billingKey, { price: `€${priceTotal}` })}
          </p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold">€{priceMonthly}</span>
          <span className="text-muted-foreground text-xs">{t('perMonth')}</span>
        </div>
      </div>
    </button>
  )
}

export function PaymentPage() {
  const {
    t,
    plans,
    stripeEnabled,
    isLoading,
    isRedirecting,
    isSuspended,
    selectedInterval,
    setSelectedInterval,
    handleSubscribe,
  } = usePaymentPage()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {isSuspended ? t('suspended.title') : t('title')}
          </CardTitle>
          <CardDescription>
            {isSuspended ? t('suspended.description') : t('description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isSuspended && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{t('suspended.alert')}</AlertDescription>
            </Alert>
          )}

          {!isSuspended && plans.length > 0 && (
            <div className="space-y-3 pt-1">
              {PLAN_ORDER.map((interval) => {
                const plan = plans.find((p) => p.id === interval)
                if (!plan) return null
                return (
                  <PlanCard
                    key={interval}
                    plan={plan}
                    isSelected={selectedInterval === interval}
                    onSelect={() => setSelectedInterval(interval)}
                    t={t}
                  />
                )
              })}
            </div>
          )}

          <ul className="space-y-2">
            {FEATURES.map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <Check className="size-4 text-green-500" />
                <span className="text-sm">{t(`features.${feature}`)}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={handleSubscribe}
            disabled={isRedirecting || !stripeEnabled}
            className="w-full"
            size="lg"
          >
            {isRedirecting ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>
                <CreditCard className="mr-2 size-4" />
                {isSuspended ? t('updatePaymentButton') : t('subscribeButton')}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
